

import classNames from 'classnames';
import React, {
  useRef,
  forwardRef,
  memo,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

const doRound = (n) => {
    return Math.round(n * 100) / 100;
};

const ElementAttributes = ({
    config,
    element,
    onChange,
    units = 'mm',
    debug = false,
}) => {
    const ppmm = config.preview.width / config.item.width;
    const elementType = element.get('elementType');

    const conversion = units === 'in' ? 25.4 : 1;

    const scale = element.get('scale', 1);

    const width = element.get('w') * scale;
    const height = element.get('h') * scale;

    const x = element.get('x');
    const left = (config.print.width - width) / 2 + x;
    const right = (config.print.width - width) / 2 - x;

    
    const y = element.get('y');
    const top = (config.print.height - height) / 2 + y;
    const bottom = (config.print.height - height) / 2 - y;

    const rotation = element.get('rotation', 0);
    const radians = rotation * (Math.PI / 180);

    const rh = Math.abs(Math.sin(radians) * element.get('w')) + Math.abs(Math.cos(radians) * element.get('h'));
    const rw = Math.abs(Math.cos(radians) * element.get('w')) + Math.abs(Math.sin(radians) * element.get('h'));

    return (
        <>
            <div className='t-flex t-flex-col t-gap-2'>
                {units === 'in' && <div className='text-warning'>Conversion from inches to millimeters might result in minor changes to inputs.</div>}
                <div className='t-flex t-gap-2'>
                    <Input label='Width' value={width / conversion} onChange={(w) => onChange({ scale: w * conversion / element.get('w'), elementType })} {...{units}}/>
                    <Input label='Height' value={height / conversion} onChange={(h) => onChange({ scale: h * conversion / element.get('h'), elementType })} {...{units}}/>
                </div>
                <div className='t-flex t-gap-2'>
                    <Input label='X' value={x / conversion} onChange={(x) => onChange({ x: x * conversion, elementType })} {...{units}}/>
                    <Input label='Y' value={y / conversion} onChange={(y) => onChange({ y: y * conversion, elementType })} {...{units}}/>
                </div>
                <div className='t-flex t-gap-2'>
                    <Input label='Left' value={left / conversion} onChange={(l) => onChange({ x: l * conversion - (config.print.width - width) / 2, elementType })} {...{units}}/>
                    <Input label='Right' value={right / conversion} onChange={(r) => onChange({ x: (config.print.width - width) / 2 - r * conversion, elementType })} {...{units}}/>
                </div>
                <div className='t-flex t-gap-2'>
                    <Input label='Top' value={top / conversion} onChange={(t) => onChange({ y: t * conversion - (config.print.height - height) / 2, elementType })} {...{units}}/>
                    <Input label='Bottom' value={bottom / conversion} onChange={(b) => onChange({ y: (config.print.height - height) / 2 - b * conversion, elementType })} {...{units}}/>
                </div>
                <div className='t-flex t-gap-2'>
                    <Input label='Rotation' value={rotation} onChange={(rotation) => onChange({ rotation, elementType })} units={null}/>
                    <div className='t-basis-1/2'/>
                </div>
                <div className='t-flex t-gap-2 t-mt-2'>
                    <Button onClick={() => onChange({ x: 0, y: 0, scale: 1, elementType })}>Reset</Button>
                    <Button onClick={() => onChange({ x: 0, y: 0, elementType })}>Center</Button>
                </div>
                <div className='t-flex t-gap-2'>
                    <Button onClick={() => onChange({ scale: config.print.width / rw, elementType })}>Fill X</Button>
                    <Button onClick={() => onChange({ scale: config.print.height / rh, elementType })}>Fill Y</Button>
                </div>
                <div className='t-flex t-gap-2'>
                    <Button onClick={() => onChange({ rotation: (rotation - 90), elementType })}>&lt;90&deg;</Button>
                    <Button onClick={() => onChange({ rotation: (rotation + 90), elementType })}>90&deg;&gt;</Button>
                </div>
            </div>

                
            {debug && <div className='t-whitespace-pre t-border t-p-2 t-mt-4 t-bg-[#eee] t-font-mono'>
                {prettyPrint(element)}
            </div>}
        </>
    );
};

const prettyPrint = (o) => {
    const { url, ...value } = !!o.toJS ? o.toJS() : o;

    return JSON.stringify(value, null, 2);
};

const parseInput = (value) => doRound(value).toString();

const Button = ({ onClick, children }) => {
    return (
        <button onClick={onClick} className='t-inline-flex t-cursor-pointer t-items-center t-justify-center t-rounded-button t-font-body t-font-bold t-uppercase t-duration-300 focus:t-outline-none active:t-outline-none t-w-full t-text-xs t-px-3 t-py-2 t-border-2 t-border-solid t-border-[var(--primary-button-bg-color)] !t-bg-[var(--primary-button-bg-color)] t-text-[var(--primary-button-text-color)] t-no-underline hover:t-text-[var(--primary-button-text-color)] hover:t-no-underline hover:t-opacity-75'>
            {children}
        </button>
    );
};

const Input = ({
    label,
    value: inputValue,
    onChange = (v) => {},
    units = 'mm',
}) => {
    const inputRef = useRef(null);
    const [ value, setValue] = useState(parseInput(inputValue));

    const handleChange = (e) => {
        const value = e.target.value;
        
        const parsed = parseFloat(value);
        setValue(e.target.value);
        if (!isNaN(parsed)) {
            onChange(doRound(parsed));
        }
    };

    const handleBlur = () => {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
            setValue(parseInput(inputValue));
        }
    };

    useEffect(() => {
        setValue(parseInput(inputValue));
    }, [ inputValue ]);

    return (
        <div className='t-flex t-flex-col t-gap-1 t-grow'>
            <label className='t-text-[#999] t-mr-2'>{label}</label>
            <div className='t-flex t-items-center'>
                <input ref={inputRef} type='text' value={value} className='form-control custom-form-control !t-rounded-lg t-border !t-border-neutrals-light-grey' onFocus={() => inputRef.current.select()} onChange={handleChange} onBlur={handleBlur}/>
                {units && <span className='t-text-[#999] -t-ml-12'>{units}</span>}
            </div>
        </div>
    );
};

const ForwardedElementAttributes = forwardRef(ElementAttributes);

export default memo(ForwardedElementAttributes);